import {
  DEFAULT_PUBLICATION_TOC_HEIGHT_OPTION,
  DEFAULT_PUBLICATION_TOC_MENU_TITLE,
  DEFAULT_PUBLICATION_TOC_PAGE_TITLE,
  PublicationTocColumnLayoutOptions,
  PublicationTocHeaderHeightOptions
} from '@opengov/stories-shared/constants';
import {
  IPublication,
  IPublicationBrandings,
  IPublicationNavMenuItem,
  IPublicationTocSettings
} from '@opengov/stories-shared/types';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import isNil from 'lodash/isNil';
import React, { useEffect, useState } from 'react';

import { onlinePublication as messages } from '../../../../../locale/stories.en.locale.json';
import { ImageUploader } from '../../../../common/components/ImageUploader';
import { PUBLICATION_TOC_MAX_HEADER_FILE_SIZE } from '../../../../constants';
import { useToast } from '../../../Toast/context/useToast.context';
import { NavBarPreview } from '../NavBarPreview';
import { styles } from './TableOfContents.styles';
import { TableOfContentsPreview } from './TableOfContentsPreview';

function isTitleValid(title: string) {
  return title.length > 0;
}

type ITableOfContentsProps = {
  entitySubdomain: string;
  publication: IPublication;
  tocSettings: IPublicationTocSettings;
  publicationBrandings: IPublicationBrandings | null;
  publicationNavigationMenuItems: IPublicationNavMenuItem[];
  handleUpdateTocSettings(payload: Partial<IPublicationTocSettings>): void;
  setTocAsLandingPageOnPublication(payload: { setTocAsLandingPage: boolean }): void;
};

export function TableOfContents({
  publication,
  publicationBrandings,
  tocSettings,
  entitySubdomain,
  handleUpdateTocSettings,
  setTocAsLandingPageOnPublication
}: ITableOfContentsProps) {
  const { toggleToast } = useToast();
  const [menuTitle, setMenuTitle] = useState(
    isNil(tocSettings.menuTitle) ? DEFAULT_PUBLICATION_TOC_MENU_TITLE : tocSettings.menuTitle
  );
  const [pageTitle, setPageTitle] = useState(
    isNil(tocSettings.pageTitle) ? DEFAULT_PUBLICATION_TOC_PAGE_TITLE : tocSettings.pageTitle
  );
  const [description, setDescription] = useState(isNil(tocSettings.description) ? '' : tocSettings.description);
  const isFormDisabled = !tocSettings.showTableOfContents;

  useEffect(() => {
    if (menuTitle !== tocSettings.menuTitle) {
      setMenuTitle(tocSettings.menuTitle ?? DEFAULT_PUBLICATION_TOC_MENU_TITLE);
    }

    if (pageTitle !== tocSettings.pageTitle) {
      setPageTitle(tocSettings.pageTitle ?? DEFAULT_PUBLICATION_TOC_PAGE_TITLE);
    }

    if (description !== tocSettings.description && !isNil(tocSettings.description)) {
      setDescription(tocSettings.description);
    }
  }, [tocSettings.menuTitle, tocSettings.pageTitle, tocSettings.description]);

  return (
    <Box sx={styles.container} data-test="table-of-contents-tab">
      <Box sx={styles.headerSection}>
        <Typography variant="h6" sx={styles.header}>
          {messages.tableOfContentsTab.header}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {messages.tableOfContentsTab.tocDescription}
        </Typography>
      </Box>

      <Box sx={styles.columnLayout}>
        <Box sx={styles.formColumn}>
          <Box sx={styles.form}>
            <FormControlLabel
              control={
                <Switch
                  id="toggle-enable-toc"
                  size="small"
                  checked={tocSettings.showTableOfContents}
                  onChange={() => {
                    handleUpdateTocSettings({
                      showTableOfContents: !tocSettings.showTableOfContents,
                      hasUserToggledToc: true
                    });
                  }}
                  inputProps={{ 'data-test': 'toggle-enable-toc' } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              }
              label={messages.tableOfContentsTab.tocToggleLabel}
            />

            <FormControlLabel
              sx={styles.setHomePageCheckbox}
              control={
                <Checkbox
                  size="small"
                  disabled={isFormDisabled}
                  checked={publication.setTocAsLandingPage}
                  onChange={(e) => setTocAsLandingPageOnPublication({ setTocAsLandingPage: !!e.target.checked })}
                  inputProps={{ 'data-test': 'set-toc-as-homepage' } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              }
              label={messages.tableOfContentsTab.setTocAsHomePage}
            />

            {/* Menu Name */}
            <Box sx={styles.formItem}>
              <Typography variant="body2" sx={styles.formItemLabel}>
                {messages.tableOfContentsTab.menuNameLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={styles.formItemDescription}>
                {messages.tableOfContentsTab.menuNameDescription}
              </Typography>
              <TextField
                sx={styles.textField}
                size="small"
                value={menuTitle}
                disabled={isFormDisabled}
                error={!isTitleValid(menuTitle)}
                helperText={!isTitleValid(menuTitle) ? messages.tableOfContentsTab.menuNameRequired : undefined}
                onChange={(e) => setMenuTitle(e.target.value)}
                onBlur={() => {
                  const trimmedMenuTitle = menuTitle.trim();
                  if (isTitleValid(trimmedMenuTitle) && trimmedMenuTitle !== tocSettings.menuTitle) {
                    handleUpdateTocSettings({ menuTitle: trimmedMenuTitle });
                  }
                  setMenuTitle(trimmedMenuTitle);
                }}
                inputProps={{
                  'data-test': 'toc-menu-name-input',
                  maxLength: 60,
                  'aria-label': messages.tableOfContentsTab.menuNameLabel,
                  className: 'no-format'
                }}
              />
            </Box>

            {/* Page Title */}
            <Box sx={styles.formItem}>
              <Typography variant="body2" sx={styles.formItemLabel}>
                {messages.tableOfContentsTab.pageTitleLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={styles.formItemDescription}>
                {messages.tableOfContentsTab.pageTitleDescription}
              </Typography>
              <TextField
                sx={styles.textField}
                size="small"
                value={pageTitle}
                disabled={isFormDisabled}
                error={!isTitleValid(pageTitle)}
                helperText={!isTitleValid(pageTitle) ? messages.tableOfContentsTab.pageTitleRequired : undefined}
                onChange={(e) => setPageTitle(e.target.value)}
                onBlur={() => {
                  if (isTitleValid(pageTitle) && pageTitle !== tocSettings.pageTitle) {
                    handleUpdateTocSettings({ pageTitle });
                  }
                }}
                inputProps={{
                  'data-test': 'toc-page-title-input',
                  maxLength: 60,
                  'aria-label': messages.tableOfContentsTab.pageTitleLabel,
                  className: 'no-format'
                }}
              />
            </Box>

            {/* Description */}
            <Box sx={styles.formItem}>
              <Typography variant="body2" sx={styles.formItemLabel}>
                {messages.tableOfContentsTab.descriptionLabel}
              </Typography>
              <TextField
                sx={styles.textField}
                size="small"
                value={description}
                disabled={isFormDisabled}
                placeholder={messages.tableOfContentsTab.descriptionPlaceholder}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={() => {
                  if (description !== tocSettings.description) {
                    handleUpdateTocSettings({ description });
                  }
                }}
                inputProps={{
                  'data-test': 'toc-description-input',
                  maxLength: 150,
                  'aria-label': messages.tableOfContentsTab.descriptionLabel,
                  className: 'no-format'
                }}
              />
            </Box>

            {/* Header Image Upload */}
            <Box sx={styles.formItem}>
              <Typography variant="body2" sx={styles.formItemLabel}>
                {messages.tableOfContentsTab.uploadHeaderTitle}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={styles.formItemDescription}>
                {messages.tableOfContentsTab.uploadHeaderDescription}
              </Typography>
              <ImageUploader
                entitySubdomain={entitySubdomain}
                maxSize={PUBLICATION_TOC_MAX_HEADER_FILE_SIZE}
                url={tocSettings.headerImageUrl ?? undefined}
                disabled={isFormDisabled}
                imagePreviewClassName="toc-header-image-preview"
                style={{ maxWidth: 500 }}
                onUploadSuccess={(url) => {
                  handleUpdateTocSettings({ headerImageUrl: url });
                  toggleToast({
                    title: messages.tableOfContentsTab.uploadSuccessToastTitle,
                    body: messages.tableOfContentsTab.uploadSuccessToastBody,
                    severity: 'success'
                  });
                }}
                requireConfirmationBeforeRemove={false}
                onRemove={() => handleUpdateTocSettings({ headerImageUrl: null })}
              />
            </Box>

            {/* Header Height */}
            <Box sx={styles.formItem}>
              <Typography variant="body2" sx={styles.formItemLabel}>
                {messages.tableOfContentsTab.headerHeightLabel}
              </Typography>
              <RadioGroup
                data-test="header-height-selection"
                value={isNil(tocSettings.headerHeightSize) ? DEFAULT_PUBLICATION_TOC_HEIGHT_OPTION : tocSettings.headerHeightSize}
                onChange={(e) => handleUpdateTocSettings({ headerHeightSize: e.target.value })}
              >
                <FormControlLabel
                  value={PublicationTocHeaderHeightOptions.Height_450px}
                  sx={styles.radioButton}
                  control={<Radio size="small" disabled={isFormDisabled} inputProps={{ 'data-test': 'header-height-450px', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>} />}
                  label="450 px"
                />
                <FormControlLabel
                  value={PublicationTocHeaderHeightOptions.Height_600px}
                  sx={styles.radioButton}
                  control={<Radio size="small" disabled={isFormDisabled} inputProps={{ 'data-test': 'header-height-600px', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>} />}
                  label={`600 px (${messages.default})`}
                />
                <FormControlLabel
                  value={PublicationTocHeaderHeightOptions.Height_720px}
                  sx={styles.radioButton}
                  control={<Radio size="small" disabled={isFormDisabled} inputProps={{ 'data-test': 'header-height-720px', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>} />}
                  label="720 px"
                />
                <FormControlLabel
                  value={PublicationTocHeaderHeightOptions.Height_900px}
                  sx={styles.radioButton}
                  control={<Radio size="small" disabled={isFormDisabled} inputProps={{ 'data-test': 'header-height-900px', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>} />}
                  label="900 px"
                />
              </RadioGroup>
            </Box>

            {/* Column Layout */}
            <Box sx={styles.formItem}>
              <Typography variant="body2" sx={styles.formItemLabel}>
                {messages.tableOfContentsTab.columnLayoutLabel}
              </Typography>
              <RadioGroup
                data-test="column-layout-selection"
                value={tocSettings.columnLayoutNumber.toString()}
                onChange={(e) => handleUpdateTocSettings({ columnLayoutNumber: Number(e.target.value) })}
              >
                <FormControlLabel
                  value={PublicationTocColumnLayoutOptions.NumColumns_1.toString()}
                  sx={styles.radioButton}
                  control={<Radio size="small" disabled={isFormDisabled} inputProps={{ 'data-test': 'column-layout-num-columns-1', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>} />}
                  label={messages.tableOfContentsTab.option1Column}
                />
                <FormControlLabel
                  value={PublicationTocColumnLayoutOptions.NumColumns_2.toString()}
                  sx={styles.radioButton}
                  control={<Radio size="small" disabled={isFormDisabled} inputProps={{ 'data-test': 'column-layout-num-columns-2', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>} />}
                  label={messages.tableOfContentsTab.option2Columns}
                />
                <FormControlLabel
                  value={PublicationTocColumnLayoutOptions.NumColumns_3.toString()}
                  sx={styles.radioButton}
                  control={<Radio size="small" disabled={isFormDisabled} inputProps={{ 'data-test': 'column-layout-num-columns-3', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>} />}
                  label={messages.tableOfContentsTab.option3Columns}
                />
              </RadioGroup>
            </Box>
          </Box>
        </Box>

        <Box sx={styles.previewColumn}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={styles.previewLabel}
          >
            {messages.tableOfContentsTab.preview}
          </Typography>

          {tocSettings.showTableOfContents ? (
            <>
              <NavBarPreview
                publicationBrandings={publicationBrandings}
                menuItems={
                  publication.setTocAsLandingPage
                    ? [
                      { displayName: tocSettings.menuTitle },
                      { displayName: 'Menu 1', children: [{ displayName: 'Child' }] },
                      { displayName: 'Menu 2' }
                    ]
                    : [
                      { displayName: 'Menu 1', children: [{ displayName: 'Child' }] },
                      { displayName: 'Menu 2' },
                      { displayName: tocSettings.menuTitle }
                    ]
                }
              />
              <TableOfContentsPreview tocSettings={tocSettings} />
            </>
          ) : (
            <Box sx={styles.noPreview}>
              <Typography color="text.secondary">
                {messages.tableOfContentsTab.nothingToPreview}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

type ITableOfContentsContainerProps = Omit<ITableOfContentsProps, 'tocSettings'> & {
  tocSettings: IPublicationTocSettings | null;
};

export function TableOfContentsContainer(props: ITableOfContentsContainerProps) {
  const { tocSettings, ...rest } = props;

  if (!tocSettings) {
    return (
      <Box sx={styles.loadingContainer} data-test="table-of-contents-tab">
        <CircularProgress role="status" aria-label="Loading" />
      </Box>
    );
  }

  return <TableOfContents {...rest} tocSettings={tocSettings} />;
}
